import _ from 'lodash';
import React from 'react';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';

import { Dimmer, Loader, Divider, Grid, Icon } from '@mairistem/composants';
import { request } from '@mairistem/tools';

import { baseUrlApi } from '@/routes';
import type { TypePacs } from '@/types';
import { useEntiteStore } from '@/zustand';

import PacsModal from '../../Modals/PacsModal';

interface StatistiqueMois {
  mois: string;
  conclusion: number;
  modification: number;
  dissous: number;
  irrecevable: number;
}

interface BlocTableauStatistiquesProps {
  selectedYear: number;
}

const MOIS_ABBR = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];

const BlocTableauStatistiques = (props: BlocTableauStatistiquesProps) => {
  const { selectedYear } = props;

  const [dateSelectedPacs, setDateSelectedPacs] = React.useState<string | null>(
    null,
  );
  const [typeSelectedPacs, setTypeSelectedPacs] =
    React.useState<TypePacs | null>(null);
  const [openPacsModal, setOpenPacsModal] = React.useState(false);
  const [titleModal, setTitleModal] = React.useState('');
  const [statistiques, setStatistiques] = React.useState<StatistiqueMois[]>([]);
  const [loading, setLoading] = React.useState(false);
  const blocked = useEntiteStore((state) => state.blocked);

  React.useEffect(() => {
    if (!blocked) {
      setLoading(true);
      request
        .get(`${baseUrlApi}/pacs/statistiques/${selectedYear}`)
        .then((response: any) => {
          setStatistiques(response);
        })
        .catch(() => {
          setStatistiques([]);
          toast.error(
            `Impossible de charger les statistiques de l'année ${selectedYear}`,
          );
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [selectedYear, blocked]);

  const totals = React.useMemo(
    () => ({
      enCours: statistiques.map((s) => s.conclusion + s.modification),
      conclusion: statistiques.map((s) => s.conclusion),
      modification: statistiques.map((s) => s.modification),
      dissous: statistiques.map((s) => s.dissous),
      irrecevable: statistiques.map((s) => s.irrecevable),
    }),
    [statistiques],
  );

  const sum = (arr: number[]) => arr.reduce((a, b) => a + b, 0);

  const rowStyle = {
    style: {
      padding: 0,
    },
  };

  const cellStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
  };

  const columnDividerStyle = {
    borderRight: '1px solid #d4d4d5',
    width: 50,
    height: 40,
    padding: 0,
    margin: 0,
  };

  const dividerStyle = {
    style: {
      margin: 0,
      backgroundColor: 'lightgrey',
    },
  };

  const clickableStyle = {
    cursor: 'pointer',
    textDecoration: 'underline',
  };

  const renderStatValue = (
    value: number,
    monthIndex: number,
    label: TypePacs | '',
    bold = false,
  ) => {
    const handleClick = () => {
      if (value > 0) {
        const isTotalAnnee = monthIndex === -1;
        const dateStr = `${selectedYear}-${String(monthIndex + 1).padStart(2, '0')}`;

        setDateSelectedPacs(dateStr);
        setTypeSelectedPacs(label || null);
        setOpenPacsModal(true);

        // construction du titre du modal
        const nomMois = !isTotalAnnee ? dayjs(dateStr).format('MMMM') : '';
        switch (label) {
          case 'En-cours':
            setTitleModal(
              isTotalAnnee
                ? `Tous les PACS en cours en ${selectedYear}`
                : `Pacs en cours en ${nomMois} ${selectedYear}`,
            );
            break;
          case 'Conclusion':
            setTitleModal(
              isTotalAnnee
                ? `Toutes les conclusions de PACS en ${selectedYear}`
                : `Conclusions de PACS en ${nomMois} ${selectedYear}`,
            );
            break;
          case 'Modification':
            setTitleModal(
              isTotalAnnee
                ? `Toutes les modifications de PACS en ${selectedYear}`
                : `Modifications de PACS en ${nomMois} ${selectedYear}`,
            );
            break;
          case 'Dissous':
            setTitleModal(
              isTotalAnnee
                ? `Tous les PACS dissous en ${selectedYear}`
                : `Pacs dissous en ${nomMois} ${selectedYear}`,
            );
            break;
          case 'Irrecevable':
            setTitleModal(
              isTotalAnnee
                ? `Tous les PACS irrecevables en ${selectedYear}`
                : `Pacs irrecevables en ${nomMois} ${selectedYear}`,
            );
            break;
          default:
            setTitleModal(
              isTotalAnnee
                ? `Total des PACS en ${selectedYear}`
                : `Total des PACS en ${nomMois} ${selectedYear}`,
            );
        }
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleClick();
      }
    };

    const testIdLigne = _.kebabCase(label || 'total');
    const testIdColonne =
      monthIndex === -1 ? 'total' : String(monthIndex + 1).padStart(2, '0');

    const content = (
      <span
        data-testid={`bloc-statistiques-${testIdLigne}-${testIdColonne}`}
        style={value > 0 ? clickableStyle : undefined}
        onClick={value > 0 ? handleClick : undefined}
        onKeyDown={value > 0 ? handleKeyDown : undefined}
        role={value > 0 ? 'button' : undefined}
        tabIndex={value > 0 ? 0 : undefined}
      >
        {value}
      </span>
    );

    return bold ? <b>{content}</b> : content;
  };

  return (
    <>
      {loading && (
        <Dimmer active inverted>
          <Loader content="Chargement des statistiques..." />
        </Dimmer>
      )}
      <Grid
        style={{
          margin: '0 0 0 1.5em',
        }}
      >
        {/* Header Row */}
        <Grid.Row {...rowStyle}>
          <Grid.Column style={{ flex: 1 }} />
          {MOIS_ABBR.map((m, i) => (
            <Grid.Column
              // biome-ignore lint/suspicious/noArrayIndexKey: no need
              key={`mois-${i}`}
              style={{
                width: '60px',
                ...columnDividerStyle,
                ...(i % 2 === 0
                  ? { backgroundColor: 'rgb(249, 249, 249)' }
                  : {}),
              }}
              textAlign="center"
            >
              <div style={cellStyle}>
                <b>{m}</b>
              </div>
            </Grid.Column>
          ))}
          <Grid.Column
            style={{
              width: '60px',
              ...columnDividerStyle,
              backgroundColor: 'rgb(249, 249, 249)',
            }}
            textAlign="center"
          >
            <div style={cellStyle}>
              <b>Total</b>
            </div>
          </Grid.Column>
        </Grid.Row>
        <Divider {...dividerStyle} />

        {/* Nombre de PACS en cours */}
        <Grid.Row {...rowStyle}>
          <Grid.Column
            style={{ flex: 1, display: 'flex', alignItems: 'center' }}
          >
            <Icon name="CircleCheck" iconSet="Lucide" color="green" />
            &nbsp;
            <b>Nombre de PACS en cours</b>
          </Grid.Column>
          {totals.enCours.map((v, i) => (
            <Grid.Column
              // biome-ignore lint/suspicious/noArrayIndexKey: no need
              key={`en-cours-${i}`}
              style={{
                width: '60px',
                ...columnDividerStyle,
                ...(i % 2 === 0
                  ? { backgroundColor: 'rgb(249, 249, 249)' }
                  : {}),
              }}
              textAlign="center"
            >
              <div style={cellStyle}>
                {renderStatValue(v, i, 'En-cours', true)}
              </div>
            </Grid.Column>
          ))}
          <Grid.Column
            style={{
              width: '60px',
              ...columnDividerStyle,
              backgroundColor: 'rgb(249, 249, 249)',
            }}
            textAlign="center"
          >
            <div style={cellStyle}>
              {renderStatValue(sum(totals.enCours), -1, 'En-cours', true)}
            </div>
          </Grid.Column>
        </Grid.Row>
        <Divider {...dividerStyle} />

        {/* Conclusion de PACS */}
        <Grid.Row {...rowStyle}>
          <Grid.Column
            style={{ flex: 1, display: 'flex', alignItems: 'center' }}
          >
            &nbsp;&nbsp;&nbsp;
            <Icon name="circle" size="tiny" color="grey" />
            &nbsp;Conclusion de PACS
          </Grid.Column>
          {totals.conclusion.map((v, i) => (
            <Grid.Column
              // biome-ignore lint/suspicious/noArrayIndexKey: no need
              key={`conclusion-${i}`}
              style={{
                width: '60px',
                ...columnDividerStyle,
                ...(i % 2 === 0
                  ? { backgroundColor: 'rgb(249, 249, 249)' }
                  : {}),
              }}
              textAlign="center"
            >
              <div style={cellStyle}>{renderStatValue(v, i, 'Conclusion')}</div>
            </Grid.Column>
          ))}
          <Grid.Column
            style={{
              width: '60px',
              ...columnDividerStyle,
              backgroundColor: 'rgb(249, 249, 249)',
            }}
            textAlign="center"
          >
            <div style={cellStyle}>
              {renderStatValue(sum(totals.conclusion), -1, 'Conclusion')}
            </div>
          </Grid.Column>
        </Grid.Row>
        <Divider {...dividerStyle} />

        {/* Modification de PACS */}
        <Grid.Row {...rowStyle}>
          <Grid.Column
            style={{ flex: 1, display: 'flex', alignItems: 'center' }}
          >
            &nbsp;&nbsp;&nbsp;
            <Icon name="circle" size="tiny" color="grey" />
            &nbsp;Modification de PACS
          </Grid.Column>
          {totals.modification.map((v, i) => (
            <Grid.Column
              // biome-ignore lint/suspicious/noArrayIndexKey: no need
              key={`modification-${i}`}
              style={{
                width: '60px',
                ...columnDividerStyle,
                ...(i % 2 === 0
                  ? { backgroundColor: 'rgb(249, 249, 249)' }
                  : {}),
              }}
              textAlign="center"
            >
              <div style={cellStyle}>
                {renderStatValue(v, i, 'Modification')}
              </div>
            </Grid.Column>
          ))}
          <Grid.Column
            style={{
              width: '60px',
              ...columnDividerStyle,
              backgroundColor: 'rgb(249, 249, 249)',
            }}
            textAlign="center"
          >
            <div style={cellStyle}>
              {renderStatValue(sum(totals.modification), -1, 'Modification')}
            </div>
          </Grid.Column>
        </Grid.Row>
        <Divider {...dividerStyle} />

        {/* Nombre de PACS dissous */}
        <Grid.Row {...rowStyle}>
          <Grid.Column
            style={{ flex: 1, display: 'flex', alignItems: 'center' }}
          >
            <Icon name="CircleX" iconSet="Lucide" color="red" />
            &nbsp;
            <b>Nombre de PACS dissous</b>
          </Grid.Column>
          {totals.dissous.map((v, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <Grid.Column
              // biome-ignore lint/suspicious/noArrayIndexKey: no need
              key={`dissous-${i}`}
              style={{
                width: '60px',
                ...columnDividerStyle,
                ...(i % 2 === 0
                  ? { backgroundColor: 'rgb(249, 249, 249)' }
                  : {}),
              }}
              textAlign="center"
            >
              <div style={cellStyle}>
                {renderStatValue(v, i, 'Dissous', true)}
              </div>
            </Grid.Column>
          ))}
          <Grid.Column
            style={{
              width: '60px',
              ...columnDividerStyle,
              backgroundColor: 'rgb(249, 249, 249)',
            }}
            textAlign="center"
          >
            <div style={cellStyle}>
              {renderStatValue(sum(totals.dissous), -1, 'Dissous', true)}
            </div>
          </Grid.Column>
        </Grid.Row>
        <Divider {...dividerStyle} />

        {/* Nombre de PACS irrecevable */}
        <Grid.Row {...rowStyle}>
          <Grid.Column
            style={{ flex: 1, display: 'flex', alignItems: 'center' }}
          >
            <Icon name="Ban" iconSet="Lucide" color="#a333c8" />
            &nbsp;
            <b>Nombre de PACS irrecevable</b>
          </Grid.Column>
          {totals.irrecevable.map((v, i) => (
            <Grid.Column
              // biome-ignore lint/suspicious/noArrayIndexKey: no need
              key={`irrecevable-${i}`}
              style={{
                width: '60px',
                ...columnDividerStyle,
                ...(i % 2 === 0
                  ? { backgroundColor: 'rgb(249, 249, 249)' }
                  : {}),
              }}
              textAlign="center"
            >
              <div style={cellStyle}>
                {renderStatValue(v, i, 'Irrecevable', true)}
              </div>
            </Grid.Column>
          ))}
          <Grid.Column
            style={{
              width: '60px',
              ...columnDividerStyle,
              backgroundColor: 'rgb(249, 249, 249)',
            }}
            textAlign="center"
          >
            <div style={cellStyle}>
              {renderStatValue(
                sum(totals.irrecevable),
                -1,
                'Irrecevable',
                true,
              )}
            </div>
          </Grid.Column>
        </Grid.Row>
        <Divider {...dividerStyle} />

        {/* Total par colonne ROW */}
        <Grid.Row {...rowStyle}>
          <Grid.Column
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              paddingLeft: '3em',
            }}
          >
            <b>Total des déclarations</b>
          </Grid.Column>
          {MOIS_ABBR.map((_, i) => (
            <Grid.Column
              // biome-ignore lint/suspicious/noArrayIndexKey: no need
              key={`mois-${i}`}
              style={{
                width: '60px',
                ...columnDividerStyle,
                ...(i % 2 === 0
                  ? { backgroundColor: 'rgb(249, 249, 249)' }
                  : {}),
              }}
              textAlign="center"
            >
              <div style={cellStyle}>
                <b>
                  {renderStatValue(
                    sum([
                      totals.conclusion[i],
                      totals.modification[i],
                      totals.dissous[i],
                      totals.irrecevable[i],
                    ]),
                    i,
                    '',
                    true,
                  )}
                </b>
              </div>
            </Grid.Column>
          ))}
          <Grid.Column
            style={{
              width: '60px',
              ...columnDividerStyle,
              backgroundColor: 'rgb(249, 249, 249)',
              textAlign: 'center',
            }}
          >
            <div style={cellStyle}>
              <b>
                {renderStatValue(
                  sum(totals.conclusion) +
                    sum(totals.modification) +
                    sum(totals.dissous) +
                    sum(totals.irrecevable),
                  -1,
                  '',
                  true,
                )}
              </b>
            </div>
          </Grid.Column>
        </Grid.Row>
      </Grid>
      <PacsModal
        title={titleModal}
        datePacs={dateSelectedPacs}
        typePacs={typeSelectedPacs}
        open={openPacsModal}
        stats
        onClose={() => {
          setDateSelectedPacs(null);
          setTypeSelectedPacs(null);
          setOpenPacsModal(false);
        }}
      />
    </>
  );
};

export default BlocTableauStatistiques;
